/**
 * The numbers behind the full-screen chest reveal (art wow pass Phase 2, ui.md F2 / "chest slot machine"), kept pure
 * so they are unit-tested (test/revealPlan.test.ts) and so the escalation by rarity lives in one table.
 *
 * rank: 0 common, 1 uncommon, 2 rare, 3 epic, 4 legendary, 5 mythic (VFX.CHEST_RANK order).
 */
export const RANK_MAX = 5;

const clampRank = (rank: number): number => Math.max(0, Math.min(RANK_MAX, Math.round(rank)));

/**
 * How long the chest waits for each tap before it takes the tap itself: 0.9 s × (1 + 0.25·rank) (ui.md F2's charge
 * time), so a player (or a headless capture) that never taps is never stuck, and a mythic still gets its full 2 s.
 */
export function chargeIdleSeconds(rank: number): number { return 0.9 * (1 + 0.25 * clampRank(rank)); }

/** The rays' level behind the chest after `tapsDone` of `taps` rattles: 0.05 idle → 0.35 on the last one. */
export function chargeRays(tapsDone: number, taps: number): number {
  const t = taps <= 0 ? 1 : Math.max(0, Math.min(1, tapsDone / taps));
  return 0.05 + 0.3 * t;
}

/**
 * The burst, escalated by rank. `rays`: the ray shader's level at the flash (the look session tunes the stage's
 * tone/bloom so this stays coloured, not white); `flashMs`: how long it holds; `screenPeak`: the DOM white flash's
 * peak opacity (kept ≤ 0.6 — a full-white frame on a phone at night is a slap, and repeated full flashes are a
 * photosensitivity hazard); `dolly`: how far the camera pushes toward the chest (world units).
 */
export function burstPlan(rank: number): { rays: number; flashMs: number; screenPeak: number; dolly: number } {
  const r = clampRank(rank);
  return { rays: 0.32 + 0.05 * r, flashMs: 220 + 60 * r, screenPeak: 0.16 + 0.04 * r, dolly: 0.22 + 0.03 * r };
}

/** Reward cards leave the chest this far apart and fly for this long (ui.md F2: 420 ms at 140 ms stagger). */
export const CARD_FLIGHT = { ms: 420, stagger: 140, flipMs: 380, flipAfter: 60 } as const;

/**
 * The chest stage's show, escalated by rank (render/chestShow.ts): how many coins and gem chips the fountain throws and
 * how hard, the pillar of light's height (world units) and how bright it settles, the shockwaves at the burst, the
 * sigil's size on the floor, and the drop (height the chest falls from, s it takes).
 */
export function stagePlan(rank: number): {
  coins: number; gems: number; power: number; pillarHeight: number; pillarRest: number; waves: number; sigil: number; dropFrom: number; dropS: number;
} {
  const r = clampRank(rank);
  return {
    coins: 14 + 10 * r, gems: r >= 2 ? 3 * r : 0, power: 0.75 + 0.07 * r, pillarHeight: 2.6 + 0.5 * r, pillarRest: 0.1 + 0.04 * r,
    waves: 1 + Math.ceil(r / 2), sigil: 0.72 + 0.06 * r, dropFrom: 2.4 + 0.3 * r, dropS: 0.36 + 0.02 * r,
  };
}

/**
 * The rarity climb (chests v8 wow pass): the ranks the chest shows on its way to `rank`, from common. Every tap but the
 * last moves it one step up this path — the chest swaps for the next tier's with a flash — and the last tap opens it.
 * Common has nothing to climb; up to epic it climbs through every tier it passes or nearly (epic skips uncommon); the
 * top two jump in bigger steps so the taps stay four at most. The roll was decided before the chest appeared: this is
 * only how it is shown.
 */
export function climbPath(rank: number): readonly number[] {
  const r = clampRank(rank);
  return ([[0], [0, 1], [0, 1, 2], [0, 2, 3], [0, 2, 4], [0, 2, 4, 5]] as const)[r];
}

/** The playback rate of the rattle on tap `i` (it climbs a little with each tap) and of the tier-up into `rank`. */
export function rattlePitch(i: number): number { return 1 + 0.1 * Math.max(0, Math.min(4, i)); }
export function tierUpPitch(rank: number): number { return [1, 1, 1.12, 1.26, 1.41, 1.6][clampRank(rank)]; }

/**
 * The reward cards' reveal, one at a time (chests v8 wow pass): the order (the loot card first, then the gear from the
 * worst to the best, so the best comes last) and, per card, how long it is held glowing before it flips (ms) — epic and
 * up are held, and the best card, when it is epic or better, longest of all.
 */
export function cardOrder(ranks: readonly number[]): number[] {
  return ranks.map((r, i) => ({ r, i })).sort((a, b) => a.r - b.r || a.i - b.i).map((x) => x.i);
}
export function cardHoldMs(rank: number, isBest: boolean): number {
  const r = clampRank(rank);
  if (r < 3) return 0;
  return 380 + 140 * (r - 3) + (isBest ? 260 : 0);
}
/** The pause between one card's flip and the next one's (ms). */
export const CARD_BEAT_MS = 240;
